
import React, { useState, useEffect } from 'react';
import { CircularProgress, IconButton, Avatar, Paper, Container, Typography, Box, CardMedia, Link, TextField, Button, Modal, styled } from '@mui/material';
import { Footer } from '../0_layout/Footer';
import { Header } from '../0_layout/HeaderFooter';
import { useLocation } from 'react-router-dom';
//import axios from 'axios';
import { ThumbUp, ThumbUpOutlined, ThumbDown, ThumbDownOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { LoginCard, StyledCard, ContentBox } from '../7_Login/LoginPage'


// Styled green spinner
const LightGreenSpinner = styled(CircularProgress)({
  color: '#66bb6a',
  filter: 'drop-shadow(0 0 6px #66bb6a)',
});

const Comment = ({ commentId, username, content, profilePic, likes, dislikes, setShowLoginModal }) => {
  // console.log("setShowLoginModal in Comment:", setShowLoginModal);

  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [likeCount, setLikeCount] = useState(likes);
  const [dislikeCount, setDislikeCount] = useState(dislikes);
  //const navigate = useNavigate();

  // Check if the user has already liked/disliked (store user preferences in localStorage)
  useEffect(() => {
    const userReaction = localStorage.getItem(`reaction_${commentId}`);
    if (userReaction === 'like') setLiked(true);
    if (userReaction === 'dislike') setDisliked(true);
  }, [commentId]);

  const handleReaction = async (type) => {
    console.log(type + ' clicked');
    localStorage.setItem("url", window.location.pathname + window.location.search);
    const token = localStorage.getItem('token'); // Check if token exists
    if (!token) {
      setShowLoginModal(true)
      return;
    }

    const expiry = localStorage.getItem('expiryToken');
    if (Date.now() >= expiry) {
      setShowLoginModal(true)
    }

    try {
      let url = `${process.env.REACT_APP_API_URL}/api/reactions`;
      let method = 'POST';
      let bodyData = { commentId, type };

      // If user clicks on an active like/dislike, remove it
      if ((type === 'like' && liked) || (type === 'dislike' && disliked)) {
        url = `${process.env.REACT_APP_API_URL}/api/removereaction`;
        bodyData = { commentId }; // No type needed for removal
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(bodyData),
      });

      if (response.ok) {
        if (type === 'like') {
          if (liked) {
            setLikeCount(likeCount - 1); // Removing like
            localStorage.removeItem(`reaction_${commentId}`);
          } else {
            setLikeCount(likeCount + 1); // Adding like
            if (disliked) {
              setDislikeCount(dislikeCount - 1);
              setDisliked(false);
            }
            localStorage.setItem(`reaction_${commentId}`, 'like');
          }
          setLiked(!liked);
        } else if (type === 'dislike') {
          if (disliked) {
            setDislikeCount(dislikeCount - 1); // Removing dislike
            localStorage.removeItem(`reaction_${commentId}`);
          } else {
            setDislikeCount(dislikeCount + 1); // Adding dislike
            if (liked) {
              setLikeCount(likeCount - 1);
              setLiked(false);
            }
            localStorage.setItem(`reaction_${commentId}`, 'dislike');
          }
          setDisliked(!disliked);
        }
      } else {
        console.error('Failed to send reaction');
      }
    } catch (error) {
      console.error('Error sending reaction:', error);
    }
  };
  const defaultAvatar = 'https://res.cloudinary.com/dvqdylye6/image/upload/v1747637018/default-avatar-profile-icon-of-social-media-user-vector_iomgol.jpg';
  return (
    <Paper elevation={3} sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'flex-start', borderRadius: '10px' }}>
      <Avatar
        src={profilePic || defaultAvatar}
        sx={{ width: 35, height: 35, mr: 2 }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '18px' }}>{username}</Typography>
        <Typography variant="body1" sx={{ mb: 1, fontSize: '18px' }}>{content}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => handleReaction('like')} color={liked ? "primary" : "default"}>
            {liked ? <ThumbUp sx={{ fontSize: 18 }} /> : <ThumbUpOutlined sx={{ fontSize: 18 }} />}
          </IconButton>
          <Typography variant="body2" sx={{ mr: 2, fontSize: '14px' }}>{likeCount}</Typography>

          <IconButton onClick={() => handleReaction('dislike')} color={disliked ? "error" : "default"}>
            {disliked ? <ThumbDown sx={{ fontSize: 18 }} /> : <ThumbDownOutlined sx={{ fontSize: 18 }} />}
          </IconButton>
          <Typography variant="body2" sx={{ fontSize: '14px' }}>{dislikeCount}</Typography>
        </Box>
      </Box>
    </Paper>
  );
};


const CommentList = ({ articleId, newComment, setShowLoginModal }) => {
  // console.log("setShowLoginModal in CommentList:", setShowLoginModal);
  const [comments, setComments] = useState([]);
  const [reactions, setReactions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Function to add a new comment to the list
  // const addNewComment = (newComment) => {
  //   setComments((prevComments) => [newComment, ...prevComments]);
  // };

  useEffect(() => {
    const fetchCommentsAndReactions = async () => {
      try {
        // Fetch all comments for the article
        const commentsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/comments/${articleId}`);
        let commentsData = await commentsResponse.json();
        const comments = commentsData.comments.filter(comment => comment.post === true);

        // Fetch all reactions (likes & dislikes)
        const reactionsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/reactions`);
        const reactionsData = await reactionsResponse.json();

        if (!commentsResponse.ok || !reactionsResponse.ok) {
          throw new Error(commentsData.message || reactionsData.message || 'Failed to load data.');
        }

        setComments(comments);

        // Process reactions: Group by commentId
        const reactionCounts = {};
        reactionsData.reactions.forEach(({ commentId, type }) => {
          if (!reactionCounts[commentId]) {
            reactionCounts[commentId] = { likes: 0, dislikes: 0 };
          }
          if (type === 'like') reactionCounts[commentId].likes += 1;
          if (type === 'dislike') reactionCounts[commentId].dislikes += 1;
        });

        setReactions(reactionCounts);
      } catch (err) {
        setError(err.message || 'Failed to load comments.');
      } finally {
        setLoading(false);
      }
    };

    fetchCommentsAndReactions();
  }, [articleId]);

  if (loading) return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="300px"
    >
      <LightGreenSpinner size={50} thickness={5} />
      <Typography variant="h6" mt={2}>
        Loading...
      </Typography>
    </Box>
  );
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ mt: 4 }}>
      {newComment ?
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          Comments ({comments.length + 1})
        </Typography> :
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          Comments ({comments.length})
        </Typography>}
      {newComment && <Comment
        key={1}
        commentId={'123456'}
        username={localStorage.getItem('username')}
        content={newComment}
        profilePic={`https://api.dicebear.com/7.x/identicon/svg?seed=${localStorage.getItem('username')}`}
        likes={0}
        dislikes={0}
        setShowLoginModal={setShowLoginModal}
      ></Comment>}
      {comments.length > 0 ? (
        comments.map((comment) => (
          <Comment
            key={comment._id}
            commentId={comment._id}
            username={comment.username}
            content={comment.content}
            profilePic={`https://api.dicebear.com/7.x/identicon/svg?seed=${comment.username}`} // Generates unique avatar
            likes={reactions[comment._id]?.likes || 0}
            dislikes={reactions[comment._id]?.dislikes || 0}
            setShowLoginModal={setShowLoginModal}
          />
        ))
      ) : (
        !newComment && <Typography sx={{ color: 'grey' }}>No comments yet. Be the first to comment!</Typography>
      )}
    </Box>
  );
};




const BlogDetails = ({ preview }) => {
  // console.log('blog passed props: '+JSON.stringify(preview))
  const { id } = useParams();
  const location = useLocation();
  //const [article, setArticle] = useState(null);

  // const navigate = useNavigate();
  const [blog, setBlog] = useState(preview || null);

  //const [blog, setBlog] = useState(preview);
  const [loading, setLoading] = useState(preview ? false : true);
  const [error, setError] = useState("");
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [comment, setComment] = useState("");
  const [newComment, setNewComment] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);

  // console.log('blog filled preview: '+JSON.stringify(blog))

  useEffect(() => {
    if (preview) {
      console.log("Updating blog with preview data");
      setBlog(preview);
    }
  }, [preview]);


  useEffect(() => {
    const fetchArticle = async () => {
      const type = location.pathname.includes('/news/') ? 'news' : 'blog';

      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/${type}/${id}`);
        if (!res.ok) throw new Error("Failed to fetch article");
        const data = await res.json();
        setBlog(data); // <-- previously: setArticle(data)
        setLikeCount(data.likes || 0);
        setDislikeCount(data.dislikes || 0);

        setLoading(false); // <-- make sure to stop loading here
      } catch (err) {
        console.error("❌ Failed to fetch article:", err);
        setError("Failed to fetch article");
        setLoading(false);
      }
    };

    if (!preview) {
      fetchArticle();
    }
  }, [id, location.pathname]);


  const handleLeaveCommentClick = () => {
    const token = localStorage.getItem("token");
    localStorage.setItem("url", window.location.pathname + window.location.search);

    if (!token) {
      setShowLoginModal(true);
    } else {
      setShowCommentForm(!showCommentForm);
    }
    const expiry = localStorage.getItem('tokenExpiry')
    if (isTokenExpired(expiry)) {
      setShowLoginModal(true)
    }
  };

  function isTokenExpired(expiryTimestamp) {
    const currentTimestamp = Date.now(); // Get current time in milliseconds
    return currentTimestamp >= expiryTimestamp;
  }

  //   const handleCommentSubmit = async () => {
  //     if (!comment) {
  //       setError("Comment is required.");
  //       return;
  //     }
  //     setNewComment(comment);

  //     try {
  //       const response = await fetch(`${process.env.REACT_APP_API_URL}/api/comments`, {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
  //         body: JSON.stringify({ content: comment, articleId: blog._id, post: true }),
  //       });

  //       if (response.ok) {
  //         setSuccessMessage("Comment posted successfully!");
  //         setComment("");
  //         setError("");
  //         setNewComment(""); // Clear temporary comment
  // //window.location.reload(); // 🔁 refresh to reload all posted comments
  //       } else {
  //         setShowLoginModal(true)
  //         // setError("Session Expired. Login Againnn.");
  //       }
  //     } catch (error) {
  //       setError("Failed to post comment.");
  //     }
  //   };
  const handleCommentSubmit = async () => {
    if (!comment) {
      setError("Comment is required.");
      return;
    }

    const token = localStorage.getItem("token");

    // 🔐 Check if user is not logged in
    if (!token) {
      setShowLoginModal(true);
      sessionStorage.setItem("pendingComment", comment);
      return;
    }

    // 🔐 Optional: check token expiry
    const expiry = localStorage.getItem("tokenExpiry");
    if (isTokenExpired(expiry)) {
      setShowLoginModal(true);
      sessionStorage.setItem("pendingComment", comment);
      return;
    }

    setNewComment(comment);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          content: comment,
          articleId: blog._id,
          post: true
        }),
      });

      if (response.ok) {
        setSuccessMessage("Comment posted successfully!");
        setComment("");
        setError("");
        setNewComment("");
        sessionStorage.removeItem("pendingComment");
      } else {
        setShowLoginModal(true);
      }
    } catch (error) {
      setError("Failed to post comment.");
    }
  };

  const handleLikeDislike = async (type) => {
    let sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      localStorage.setItem('sessionId', sessionId);
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/blogs/${blog._id}/reaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, sessionId })
      });

      const result = await res.json();

      if (res.ok) {
        setLikeCount(result.likes);
        setDislikeCount(result.dislikes);

        // Update button states
        if (type === 'like') {
          setLiked(prev => !prev);
          setDisliked(false);
        } else {
          setDisliked(prev => !prev);
          setLiked(false);
        }
      } else {
        alert(result.message);
      }
    } catch (err) {
      console.error('Reaction error:', err);
    }
  };




  if (loading) return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="300px"
    >
      <LightGreenSpinner size={50} thickness={5} />
      <Typography variant="h6" mt={2}>
        Loading...
      </Typography>
    </Box>
  );
  const defaultAuthorImage = 'https://res.cloudinary.com/dvqdylye6/image/upload/v1747637018/default-avatar-profile-icon-of-social-media-user-vector_iomgol.jpg';

  if (error) return <Typography variant="h6" color="error" sx={{ textAlign: "center", mt: 5 }}>{error}</Typography>;

  return blog ? (
    <>
      {!preview && <Header />}
      <Container maxWidth="md" sx={{ mt: preview ? 4 : 18, mb: 4 }}>
        <CardMedia component="img" height="400" image={blog.imageURL} alt={blog.title} sx={{ borderRadius: "8px", mb: 3 }} />
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>{blog.title}</Typography>
        {/* Author image and name below title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <img
            src={blog.authorImage || defaultAuthorImage}
            alt={blog.author || 'Author'}
            style={{ width: '70px', height: '50px', borderRadius: '50%', objectFit: 'cover', marginRight: '12px' }}
          />
          <Typography variant="subtitle1" color="text.secondary">
            {blog.author}
          </Typography>
        </Box>


        <Box
          sx={{ mb: 3, lineHeight: 1.8, fontSize: '16px', color: 'text.primary' }}
          dangerouslySetInnerHTML={{ __html: blog.longDescription }}
        />

        {blog.type === 'blog' && (
          <>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
              <IconButton onClick={() => handleLikeDislike('like')} color={liked ? 'primary' : 'default'}>
                {liked ? <ThumbUp /> : <ThumbUpOutlined />}
              </IconButton>
              <Typography>{likeCount}</Typography>

              <IconButton onClick={() => handleLikeDislike('dislike')} color={disliked ? 'error' : 'default'}>
                {disliked ? <ThumbDown /> : <ThumbDownOutlined />}
              </IconButton>
              <Typography>{dislikeCount}</Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              <Link component="button" color="primary" underline="hover" onClick={handleLeaveCommentClick}>
                Leave a Comment
              </Link>{" "}
              / By {blog.author}
            </Typography>

            {showCommentForm && (
              <Box sx={{ mt: 3 }}>
                <TextField label="Your Comment" fullWidth multiline rows={4} sx={{ mb: 2 }} value={comment} onChange={(e) => setComment(e.target.value)} />
                {error && <Typography color="error">{error}</Typography>}
                {successMessage && <Typography color="success.main">{successMessage}</Typography>}
                <Button variant="contained" color="primary" onClick={handleCommentSubmit}>Post Comment</Button>
              </Box>
            )}

            {!preview && <CommentList articleId={blog._id} newComment={newComment} setShowLoginModal={setShowLoginModal} />}
          </>
        )}
      </Container>
      {!preview && <Footer />}

      {/* Login Modal */}
      <Modal open={showLoginModal} onClose={() => setShowLoginModal(false)} aria-labelledby="login-modal">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0, 0, 0, 0.5)", // Background overlay effect
          }}
          onClick={() => setShowLoginModal(false)}
        >
          <StyledCard sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center", p: 0
          }}>
            <ContentBox>
              <LoginCard />
            </ContentBox>
          </StyledCard>
        </Box>
      </Modal>

    </>
  ) : (
    <Typography variant="h6" sx={{ textAlign: "center", mt: 5 }}>Blog not found</Typography>
  );
};

export default BlogDetails;
